import React from 'react'

import { datesAreOnSameMonth } from './utils/datesAreTheSame'
import { useTranslation } from 'react-i18next'

import Button from '@mui/material/Button/Button'

import NavigateBeforeRounded from '@mui/icons-material/NavigateBeforeRounded'
import NavigateNextRounded from '@mui/icons-material/NavigateNextRounded'
import TodayRounded from '@mui/icons-material/TodayRounded'

import { Box, IconButton, Theme } from '@mui/material'
import { makeStyles } from '@mui/styles'

import Preloader from '@professocial/professocialFormComponents/FormComponents/Preloader'

interface IProps {
  loading: boolean
  setLoading?: (value: boolean) => void
  selectedDate?: number
  changeSelectedDate?: (date: number) => void
}

const useStyles = makeStyles((theme: Theme) => ({
  barContainer: {
    width: '100%',
    display: 'flex',
    position: 'relative',
    justifyContent: 'space-between',
  },
  dateNav: {
    position: 'absolute',
    left: 'calc(50% - 70px)',
    marginTop: theme.spacing(1.5),
  },
  tabsNav: {
    marginTop: theme.spacing(2),
    marginLeft: 'auto',
  },
  pagination: {
    margin: '0',
    display: 'flex',
    padding: 0,
    flexWrap: 'wrap',
    listStyle: 'none',
    alignItems: 'center',
  },
  navItem: {
    fontFamily: theme.typography.fontFamily,
    fontSize: '1rem',
    lineHeight: '17px',
  },
  navLink: {
    margin: '0 1px',
  },
  disabled: {
    opacity: 0.38,
    pointerEvents: 'none',
    cursor: 'default',
  },
  loader: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(2),
    width: '30px',
  },
}))

const PostCalendarNav: React.FC<IProps> = ({ ...props }) => {
  const classes = useStyles()
  const { t } = useTranslation()

  const Months = [
    t('coreCalendar:january'),
    t('coreCalendar:february'),
    t('coreCalendar:march'),
    t('coreCalendar:april'),
    t('coreCalendar:may'),
    t('coreCalendar:june'),
    t('coreCalendar:july'),
    t('coreCalendar:august'),
    t('coreCalendar:september'),
    t('coreCalendar:october'),
    t('coreCalendar:november'),
    t('coreCalendar:december'),
  ]

  const selectedDateObj = props.selectedDate && new Date(props.selectedDate)
  const today = new Date(Date.now())

  const onNext = () => {
    props.setLoading && props.setLoading(true)
    if (selectedDateObj) return props.changeSelectedDate && props.changeSelectedDate(selectedDateObj.setMonth(selectedDateObj.getMonth() + 1))
  }

  const onPrevious = () => {
    props.setLoading && props.setLoading(true)
    if (selectedDateObj) return props.changeSelectedDate && props.changeSelectedDate(selectedDateObj.setMonth(selectedDateObj.getMonth() - 1))
  }

  const setToday = () => {
    props.setLoading && props.setLoading(true)
    props.changeSelectedDate && props.changeSelectedDate(Date.now())
  }

  const disabled = () => {
    if (selectedDateObj) return datesAreOnSameMonth(selectedDateObj, today)
  }

  return (
    <Box className={classes.barContainer} component='div'>
      <Box className={classes.loader}>
        {props.loading ? <Preloader size={25} thickness={3} minHeight='25px' /> : null}
      </Box>
      <nav className={classes.dateNav}>
        <ul className={classes.pagination}>
          <li className={`${classes.navItem}`}>
            {selectedDateObj && `${Months[selectedDateObj.getMonth()]} ${selectedDateObj.getUTCFullYear()}`}
          </li>
          <li className={`${classes.navItem}`}>
            <IconButton
              className={`${classes.navLink}`}
              component='div'
              onClick={onPrevious}
            >
              <NavigateBeforeRounded />
            </IconButton>
          </li>
          <li className={`${classes.navItem}`}>
            <IconButton
              className={`${classes.navLink}`}
              component='div'
              onClick={onNext}
            >
              <NavigateNextRounded />
            </IconButton>
          </li>
        </ul>
      </nav>
      <Box className={classes.tabsNav} component='div'>
        <Button
          variant='text'
          size='small'
          sx={{ textTransform: 'capitalize', '& .MuiButton-iconSizeSmall': { mr: '5px', '& .MuiSvgIcon-root': { fontSize: '24px' } } }}
          disabled={disabled()}
          startIcon={<TodayRounded />}
          onClick={setToday}
        >
          {t('coreCalendar:today')}
        </Button>
      </Box>
    </Box>
  )
}

export default PostCalendarNav
